import { useEffect, useRef, useState } from 'react'
import { Mail } from 'lucide-react'
import { Sheet } from './UI'
import { safeStorage } from '../lib/safeStorage'
import { logEvent } from '../lib/analytics'
import { isOverlayBusy } from '../lib/overlayBusy'
import { sendUpgradeCode, verifyUpgradeCode, validEmail, guestDaysLeft, saveAskEarned, returnAskDue } from '../lib/guestUpgrade'
import { supabase } from '../lib/supabase'
import { noteMark, claimAsk } from '../lib/askQueue'

// ── SaveListAsk — "Keep this list" for guests, right after the first mark ─────
// 2026-10-14 (Jason: "reduce the sign-up friction"). A guest's library lives in this
// browser only; Safari clears that storage after 7 days away unless the app is
// installed, and the lease is 30 days regardless. The ask is one field, in a sheet,
// right after the first mark (the moment there's something worth keeping), queued
// behind the push ask. No password, no username, no page change: the guest is
// upgraded in place, so nothing is lost. Once per account; "Not now" costs nothing.
const KEY = (uid) => `nextep_save_ask_${uid}`

export function SaveListAsk({ user, shows = 0, delay = 6000 }) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState('email')   // email | code | done
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const timer = useRef(null)
  const uid = user?.id
  const isGuest = !!user?.is_anonymous

  // The keep-push (netlify/functions/guest-keep-push.js) lands on /profile?save=1: open
  // straight away, once-guard or not, and strip the param so a refresh doesn't re-ask.
  useEffect(() => {
    if (!uid || !isGuest) return
    try {
      const u = new URL(window.location.href)
      if (u.searchParams.get('save') !== '1') return
      u.searchParams.delete('save'); window.history.replaceState(window.history.state, '', u.toString())
      logEvent('save_ask', { result: 'shown', src: 'push' })
      setOpen(true)
    } catch {}
  }, [uid, isGuest])

  // Return visit: a guest back on a later day with a library gets the ask once the boot
  // settles, whether or not a mark ever triggered it (lib/guestUpgrade.returnAskDue).
  useEffect(() => {
    if (!uid || !isGuest || !shows) return
    if (safeStorage.get(KEY(uid))) return
    if (!returnAskDue(user, shows)) return
    const t = setTimeout(() => {
      if (isOverlayBusy() || safeStorage.get(KEY(uid))) return
      safeStorage.set(KEY(uid), new Date().toISOString())
      logEvent('save_ask', { result: 'shown', src: 'return' })
      setOpen(true)
    }, 4500)
    return () => clearTimeout(t)
  }, [uid, isGuest, shows])   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!uid || !isGuest) return
    const onOpen = (e) => { logEvent('save_ask', { result: 'shown', src: e?.detail?.src || 'event' }); setOpen(true) }
    window.addEventListener('nextep:open-save-ask', onOpen)
    return () => window.removeEventListener('nextep:open-save-ask', onOpen)
  }, [uid, isGuest])

  useEffect(() => {
    if (!uid || !isGuest) return
    const onMark = (e) => {
      const markId = noteMark(e); if (!markId) return
      if (safeStorage.get(KEY(uid))) return
      // Earned, not immediate (Phase 1): count this guest's marks; ask on the third, or from day 5.
      const n = (Number(safeStorage.get(`nextep_marks_${uid}`)) || 0) + 1
      safeStorage.set(`nextep_marks_${uid}`, String(n))
      if (!saveAskEarned(user, n)) return
      if (timer.current) return
      const tryOpen = (left) => {
        if (isOverlayBusy() && left > 0) { timer.current = setTimeout(() => tryOpen(left - 1), 800); return }
        timer.current = null
        if (isOverlayBusy()) return
        if (!claimAsk(markId)) return   // one ask per mark (lib/askQueue)
        safeStorage.set(KEY(uid), new Date().toISOString())
        logEvent('save_ask', { result: 'shown' })
        setOpen(true)
      }
      timer.current = setTimeout(() => tryOpen(40), delay)
    }
    window.addEventListener('nextep:progress-marked', onMark)
    return () => { window.removeEventListener('nextep:progress-marked', onMark); if (timer.current) clearTimeout(timer.current); timer.current = null }
  }, [uid, isGuest, delay])

  useEffect(() => {
    if (!open) return
    window.__nextepModalHold = true
    return () => { window.__nextepModalHold = false }
  }, [open])

  if (!open || !isGuest) return null
  const days = guestDaysLeft(user)
  const close = (result) => { setOpen(false); if (result) logEvent('save_ask', { result }) }

  const send = async () => {
    if (busy) return
    if (!validEmail(email)) { setErr('That doesn’t look like an email address.'); return }
    setBusy(true); setErr('')
    const r = await sendUpgradeCode(supabase, email)
    setBusy(false)
    if (r.status === 'attached') { setStep('done'); logEvent('save_ask', { result: 'attached' }); setTimeout(() => setOpen(false), 1600) /* useAuth hears USER_UPDATED */; return }
    if (r.status === 'code_sent') { setStep('code'); logEvent('save_ask', { result: 'sent' }); return }
    if (r.status === 'exists') { setErr(r.message); return }
    if (r.status === 'not_guest') { setOpen(false); return }
    setErr(r.message || 'Something went wrong. Try again.')
  }
  const verify = async () => {
    if (busy) return
    setBusy(true); setErr('')
    const r = await verifyUpgradeCode(supabase, email, code)
    setBusy(false)
    if (!r.ok) { setErr(r.message); return }
    setStep('done'); logEvent('save_ask', { result: 'verified' })
    setTimeout(() => setOpen(false), 1600) /* useAuth hears USER_UPDATED */
  }

  const field = { width: '100%', height: 48, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-strong)', fontFamily: 'var(--font)', fontSize: 16, padding: '0 14px', outline: 'none', boxSizing: 'border-box' }
  const primary = { flex: 1.2, height: 48, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontWeight: 700, fontSize: 15, cursor: busy ? 'default' : 'pointer', fontFamily: 'var(--font)', opacity: busy ? 0.7 : 1 }
  const ghost = { flex: 1, height: 48, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: 'var(--font)' }

  return (
    <Sheet open onClose={() => close('dismissed')} label="Keep this list" from="bottom" dismissible={step !== 'done'}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-full)', margin: '0 auto 10px', background: 'rgba(var(--amber-rgb),0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Mail size={24} color="var(--amber)" /></div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--amber)' }}>Guest · {days} day{days === 1 ? '' : 's'} left on this phone</div>
        <div style={{ color: 'var(--text-strong)', fontFamily: 'var(--font-serif)', fontSize: 23, lineHeight: 1.15, marginTop: 6 }}>
          {step === 'done' ? 'Saved. This list is yours.' : step === 'code' ? 'Check your email' : 'Keep this list anywhere'}
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>
          {step === 'done' ? 'Same list, every device. Nothing to remember.'
            : step === 'code' ? <>We sent a 6-digit code to <b style={{ color: 'var(--text)' }}>{email.trim()}</b>. Type it here and you’re in.</>
            : 'Right now it lives in this browser only. Add an email and it’s yours on every device. No password.'}
        </div>
      </div>
      {step === 'email' && (
        <form onSubmit={(e) => { e.preventDefault(); send() }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setErr('') }} aria-label="Email address" style={field} />
          {err && <div role="alert" style={{ color: 'var(--danger-text)', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => close('dismissed')} style={ghost}>Not now</button>
            <button type="submit" disabled={busy} style={primary}>{busy ? 'Sending…' : 'Send my code'}</button>
          </div>
        </form>
      )}
      {step === 'code' && (
        <form onSubmit={(e) => { e.preventDefault(); verify() }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={8} placeholder="6-digit code" value={code} onChange={(e) => { setCode(e.target.value); setErr('') }} aria-label="6-digit code" style={{ ...field, textAlign: 'center', letterSpacing: '0.3em', fontFamily: 'var(--font-mono)', fontSize: 20 }} />
          {err && <div role="alert" style={{ color: 'var(--danger-text)', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => { setStep('email'); setCode(''); setErr('') }} style={ghost}>Change email</button>
            <button type="submit" disabled={busy} style={primary}>{busy ? 'Checking…' : 'Save my list'}</button>
          </div>
        </form>
      )}
    </Sheet>
  )
}
